import { Icon, type IconName } from '@mdvip/shared/components/blocks/Icon';
import { MDVIPConnectLogo } from '@mdvip/shared/components/blocks/MDVIPConnectLogo';
import { Avatar, AvatarFallback } from '@mdvip/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@mdvip/shared/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from '@mdvip/shared/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetTitle } from '@mdvip/shared/components/ui/sheet';
import { cn } from '@mdvip/shared/lib/utils';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

/**
 * The app-shell navigation both portals mount: a sticky top bar that holds the wordmark, the
 * primary destinations and the account entry, plus a floating bottom tab bar that carries the
 * same destinations below `lg`. The account menu is one list rendered into two shells, a
 * dropdown on desktop and a full-bleed drawer on a phone.
 *
 * Data-agnostic on purpose: every destination, row, label and string arrives as a prop, already
 * translated and already filtered. Which sections a portal has, which of them a user may see,
 * and which namespace the strings came from are all the caller's business. That is what lets
 * members and providers share one shell without the shell knowing either of them exists.
 *
 * The design models it the same way: one `NavigationHeader` with a `variant`, one
 * `NavigationTabBar` with a `variant`, over a shared `nav-chrome` of row/drawer/tab pieces.
 */

export interface AppNavItem {
  to: string;
  icon: IconName;
  label: string;
  /** Tab-bar override for a label too long for a 10px slot. Falls back to `label`. */
  shortLabel?: string;
}

export interface AppNavRow {
  key: string;
  label: string;
  icon?: IconName;
  /** Absent means an inert placeholder row: it renders, and does nothing. */
  to?: string;
  /** Alternative to `to` for rows that open something in-page rather than navigate. */
  onSelect?: () => void;
  testId?: string;
}

export interface AppNavAccount {
  initials: string;
  name: string;
  /** The line under the name: a member ID for members, a role for providers. */
  secondaryLine?: string;
  rows: readonly AppNavRow[];
  moreRows: readonly AppNavRow[];
  /** The call row at the foot of the More group. Omitted, the row is not rendered. */
  support?: { phone: string; callLabel: string };
  onLogout: () => void;
}

export interface AppNavLegalLink {
  key: string;
  label: string;
  href: string;
}

export interface AppNavLabels {
  primary: string;
  bottomBar: string;
  accountMenu: string;
  myAccount: string;
  more: string;
  closeMenu: string;
  logout: string;
}

export interface AppNavProps {
  items: readonly AppNavItem[];
  account: AppNavAccount;
  labels: AppNavLabels;
  /**
   * The legal strip at the foot of the account menu. The portals have no footer, so these links
   * have nowhere else to live. Omitted, the strip is not rendered.
   */
  legal?: { label: string; links: readonly AppNavLegalLink[] };
  /**
   * Colour scope for the chrome, e.g. `theme-b` on the practice side. Applied to the header, the
   * tab bar and both portalled account shells, which mount outside the header and would
   * otherwise fall back to the page's ambient theme.
   */
  themeClassName?: string;
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const ACCOUNT_ROW = 'relative flex min-h-11 w-full items-center gap-4 rounded-md px-4 text-left text-sm';
const ACCOUNT_GROUP = 'flex flex-col gap-1 border-b p-4';
const ACCOUNT_GROUP_LABEL = 'px-4 pt-0 pb-1 text-xs font-semibold text-muted-foreground';
/* Log out sits flush rather than inside a padded group, so it is px-4 on the row
   itself and carries no corner radius. */
const LOGOUT_ROW = 'flex min-h-11 w-full items-center gap-4 rounded-md px-4 text-left text-sm font-medium';
/* Log out is padded like a group rather than sitting flush, so its icon lands on the same
   vertical as every row above it: 16px of group padding plus the row's own 16px. */
const LOGOUT_GROUP = 'p-4';
const LEGAL_STRIP = 'flex flex-row items-center justify-center gap-4 border-t px-4 py-4';
const LEGAL_LINK =
  'flex min-h-6 items-center text-xs text-muted-foreground underline decoration-from-font underline-offset-2 hover:text-foreground';

/* Account rows carry a visible current-page state, which the top bar deliberately
   does not. */
const rowState = (active = false) =>
  active ? 'bg-accent font-medium text-foreground' : 'text-foreground hover:bg-accent';
const rowIcon = (active = false) => (active ? 'text-primary' : 'text-muted-foreground');

/* The travelling pane is positioned against the slot list, so its width and
   offset are a fraction of the list rather than a pixel value. Enumerated
   rather than computed because Tailwind needs the class to exist at build
   time, and the item count is only ever 3 or 4. */
const LENS_WIDTH: Record<number, string> = { 3: 'w-1/3', 4: 'w-1/4' };
const LENS_OFFSET = ['translate-x-0', 'translate-x-full', 'translate-x-[200%]', 'translate-x-[300%]'];

export function AppNav({ items, account, labels, legal, themeClassName }: AppNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isActive = (path: string): boolean => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const activeIndex = items.findIndex((item) => isActive(item.to));
  const support = account.support;

  const avatar = (size: 'default' | 'lg') => (
    <Avatar size={size} className="after:hidden">
      <AvatarFallback className="nc-avatar text-xs font-bold">{account.initials}</AvatarFallback>
    </Avatar>
  );

  const rowBody = (row: AppNavRow, active: boolean) => (
    <>
      {row.icon && <Icon name={row.icon} size="lg" className={rowIcon(active)} />}
      <span className="min-w-0 flex-1 truncate" title={row.label}>
        {row.label}
      </span>
    </>
  );

  const accountBlock = (
    <div className="flex w-full items-center gap-4 border-b bg-gradient-to-br from-primary/10 via-primary/5 to-card px-4 py-4">
      {avatar('lg')}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-foreground">{account.name}</div>
        {account.secondaryLine && <div className="text-xs text-muted-foreground">{account.secondaryLine}</div>}
      </div>
    </div>
  );

  const supportHref = support && `tel:${support.phone.replace(/\./g, '')}`;
  const supportBody = support && (
    <>
      <Icon name="phone" size="md" className={rowIcon()} />
      <span className="underline decoration-from-font underline-offset-2">{support.phone}</span>
    </>
  );

  /* Plain anchors, not menu items: they navigate off the account menu entirely rather than being
     commands you pick one of, so they carry no menuitem role in either shell. */
  const legalStrip = legal && (
    <nav aria-label={legal.label} className={LEGAL_STRIP}>
      {legal.links.map((link) => (
        <a key={link.key} href={link.href} className={cn(LEGAL_LINK, FOCUS_RING)}>
          {link.label}
        </a>
      ))}
    </nav>
  );

  // ── desktop shell: a menu, so the rows are real menu items ───────────────────
  const menuRow = (row: AppNavRow) => {
    const to = row.to;
    const active = !!to && isActive(to);
    const onClick =
      to || row.onSelect
        ? () => {
            if (to) navigate(to);
            row.onSelect?.();
          }
        : undefined;
    return (
      <DropdownMenuItem
        key={row.key}
        data-testid={row.testId}
        aria-current={active ? 'page' : undefined}
        onClick={onClick}
        className={cn(ACCOUNT_ROW, rowState(active))}
      >
        {rowBody(row, active)}
      </DropdownMenuItem>
    );
  };

  const accountDropdown = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            data-testid="user-menu-trigger"
            aria-label={labels.accountMenu}
            className="relative flex min-h-11 items-center gap-2 rounded-md px-2 transition-colors hover:bg-accent"
          >
            {avatar('default')}
            <Icon name="chevronDown" size="xs" className="text-muted-foreground" />
          </button>
        }
      />
      <DropdownMenuContent align="end" className={cn('w-[312px] p-0', themeClassName)}>
        {accountBlock}
        <DropdownMenuGroup className={ACCOUNT_GROUP}>
          <DropdownMenuLabel className={ACCOUNT_GROUP_LABEL}>{labels.myAccount}</DropdownMenuLabel>
          {account.rows.map(menuRow)}
        </DropdownMenuGroup>
        <DropdownMenuGroup className={ACCOUNT_GROUP}>
          <DropdownMenuLabel className={ACCOUNT_GROUP_LABEL}>{labels.more}</DropdownMenuLabel>
          {account.moreRows.map(menuRow)}
          {support && (
            <DropdownMenuItem
              className={cn(ACCOUNT_ROW, rowState(), 'font-medium')}
              render={
                <a href={supportHref} aria-label={support.callLabel}>
                  {supportBody}
                </a>
              }
            />
          )}
        </DropdownMenuGroup>
        <DropdownMenuGroup className={LOGOUT_GROUP}>
          <DropdownMenuItem
            data-testid="logout-menu-item"
            onClick={account.onLogout}
            className={cn(LOGOUT_ROW, rowState())}
          >
            <Icon name="logOut" size="lg" className={rowIcon()} />
            <span className="min-w-0 flex-1 truncate">{labels.logout}</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        {legalStrip}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // ── phone shell: a full-bleed drawer, so the rows are plain controls ─────────
  const drawerRow = (row: AppNavRow) => {
    const to = row.to;
    const active = !!to && isActive(to);
    return (
      <button
        key={row.key}
        type="button"
        data-testid={row.testId ? `${row.testId}-mobile` : undefined}
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          setDrawerOpen(false);
          if (to) navigate(to);
          row.onSelect?.();
        }}
        className={cn(ACCOUNT_ROW, rowState(active), 'transition-colors', FOCUS_RING)}
      >
        {rowBody(row, active)}
      </button>
    );
  };

  const accountDrawer = (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          'inset-0 h-auto w-full max-w-none gap-0 overflow-y-auto overscroll-contain rounded-none border-0 shadow-2xl',
          themeClassName,
        )}
      >
        <div className="sticky top-0 z-10 flex min-h-14 items-center gap-4 border-b bg-card px-4">
          <SheetTitle className="min-w-0 flex-1 truncate text-base font-semibold text-foreground">
            {labels.myAccount}
          </SheetTitle>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            aria-label={labels.closeMenu}
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-foreground hover:bg-accent',
              FOCUS_RING,
            )}
          >
            <Icon name="x" size="lg" />
          </button>
        </div>
        {accountBlock}
        <nav aria-label={labels.myAccount} className={ACCOUNT_GROUP}>
          <div className={ACCOUNT_GROUP_LABEL}>{labels.myAccount}</div>
          {account.rows.map(drawerRow)}
        </nav>
        <nav aria-label={labels.more} className={ACCOUNT_GROUP}>
          <div className={ACCOUNT_GROUP_LABEL}>{labels.more}</div>
          {account.moreRows.map(drawerRow)}
          {support && (
            <a
              href={supportHref}
              aria-label={support.callLabel}
              onClick={() => setDrawerOpen(false)}
              className={cn(ACCOUNT_ROW, rowState(), 'font-medium transition-colors', FOCUS_RING)}
            >
              {supportBody}
            </a>
          )}
        </nav>
        {/* Log out is pinned below the groups so the answer to "how do I log out"
            is never a scroll. */}
        <div className={LOGOUT_GROUP}>
          <button
            type="button"
            data-testid="logout-menu-item-mobile"
            onClick={account.onLogout}
            className={cn(LOGOUT_ROW, rowState(), 'transition-colors', FOCUS_RING)}
          >
            <Icon name="logOut" size="lg" className={rowIcon()} />
            <span className="min-w-0 flex-1 truncate">{labels.logout}</span>
          </button>
        </div>
        {legal && (
          <nav aria-label={legal.label} className={LEGAL_STRIP}>
            {legal.links.map((link) => (
              <a
                key={link.key}
                href={link.href}
                onClick={() => setDrawerOpen(false)}
                className={cn(LEGAL_LINK, FOCUS_RING)}
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}
      </SheetContent>
    </Sheet>
  );

  return (
    <>
      {/* `app-header` is required by @mdvip/shared/features/workout-library/vendorOverrides.css
          — the PTLINKED plugin's own CSS ships an unscoped `.sticky` rule that otherwise wins
          over this element's `sticky` utility (Tailwind v4 layers vs. unlayered vendor CSS).
          Don't rename/remove this class without updating that override too. */}
      <header className={cn('app-header sticky top-0 z-40 border-b bg-card', themeClassName)}>
        <div className="content-max section-x">
          <div className="hidden min-h-16 items-center gap-4 lg:flex">
            <MDVIPConnectLogo className="h-5 w-auto shrink-0 text-foreground" />

            <div className="flex min-w-0 items-center">
              <NavigationMenu aria-label={labels.primary} className="max-w-none flex-none justify-start">
                <NavigationMenuList className="justify-start">
                  {items.map((item) => (
                    <NavigationMenuItem key={item.to}>
                      <NavigationMenuLink
                        render={<Link to={item.to} />}
                        aria-current={isActive(item.to) ? 'page' : undefined}
                        className={navigationMenuTriggerStyle()}
                      >
                        {item.label}
                      </NavigationMenuLink>
                    </NavigationMenuItem>
                  ))}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-2">{accountDropdown}</div>
          </div>

          {/* The phone top bar is the wordmark and the account entry only. The four
              destinations are in the bottom bar below, in the thumb zone. */}
          <div className="flex min-h-14 items-center gap-2 lg:hidden">
            <MDVIPConnectLogo className="h-5 w-auto shrink-0 text-foreground" />
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <button
                type="button"
                data-testid="user-menu-trigger-mobile"
                aria-label={labels.myAccount}
                aria-haspopup="dialog"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen(true)}
                className="relative flex h-11 w-11 items-center justify-center rounded-full transition-colors hover:bg-accent"
              >
                {avatar('default')}
              </button>
              {accountDrawer}
            </div>
          </div>
        </div>
      </header>

      {/* 8px top gutter + a 56px row + 16px bottom gutter = 80px, which is the
          `pb-20` every page carries to keep its last content clear of the bar. */}
      <div className={cn('fixed inset-x-0 bottom-0 z-40 lg:hidden', themeClassName)}>
        <nav aria-label={labels.bottomBar} className="nc-tabbar-safe-bottom px-4 pt-2">
          <ul className="nc-tabbar-slab relative isolate mx-auto flex max-w-2xl items-stretch overflow-hidden">
            <span
              aria-hidden="true"
              className={cn(
                'nc-tab-lens pointer-events-none absolute inset-y-0 left-0',
                LENS_WIDTH[items.length],
                LENS_OFFSET[Math.max(activeIndex, 0)],
                activeIndex < 0 && 'opacity-0',
              )}
            >
              <span className="nc-tab-pane absolute inset-y-1 left-1/2 w-[calc(100%-12px)] -translate-x-1/2" />
            </span>
            {items.map((item) => {
              const active = isActive(item.to);
              /* The slot's accessible name is its own short label, not the top bar's longer one:
                 a tab bar names each destination the way it shows it. */
              const slotLabel = item.shortLabel ?? item.label;
              return (
                <li key={item.to} className="relative flex-1">
                  <Link
                    to={item.to}
                    aria-current={active ? 'page' : undefined}
                    aria-label={slotLabel}
                    className={cn(
                      'relative flex h-14 w-full items-center justify-center',
                      active ? 'nc-tab-slot-active' : 'nc-tab-slot',
                      FOCUS_RING,
                    )}
                  >
                    <Icon
                      name={item.icon}
                      size="xl"
                      weight={active ? 'solid' : 'regular'}
                      className={cn(
                        'nc-tab-icon -translate-y-[7px]',
                        active ? 'scale-[1.03]' : 'nc-tab-icon-off scale-100',
                      )}
                    />
                    {/* The label is absolutely placed under the icon so every slot's
                        glyph holds one vertical position and the row stays level. */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        'nc-tab-label pointer-events-none absolute bottom-2.5 text-[10px] leading-none',
                        active ? 'font-semibold' : 'font-normal',
                      )}
                    >
                      {slotLabel}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </>
  );
}
